import { ref, computed } from 'vue';
import type { Ref } from 'vue';
import type { FormElement, CheckboxProperties, CheckboxOption } from './form.types';

interface UseCheckboxAlternativesModalOptions {
  selectedElement: Ref<FormElement | null | undefined>;
  updateElement: (updates: Partial<FormElement>) => void;
}

export function useCheckboxAlternativesModal(options: UseCheckboxAlternativesModalOptions) {
  const { selectedElement, updateElement } = options;

  const localAlternatives = ref<CheckboxOption[]>([]);

  const totalCount = computed(() => localAlternatives.value.length);

  let fallbackIdCounter = 0;

  function createOptionId(): string {
    const cryptoApi = globalThis.crypto;
    if (cryptoApi?.randomUUID) {
      return `opt-${cryptoApi.randomUUID()}`;
    }
    if (cryptoApi?.getRandomValues) {
      const bytes = new Uint8Array(16);
      cryptoApi.getRandomValues(bytes);
      const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
      return `opt-${hex}`;
    }
    fallbackIdCounter += 1;
    return `opt-fallback-${Date.now()}-${fallbackIdCounter}`;
  }

  function initFromElement() {
    const props = selectedElement.value?.properties as CheckboxProperties | undefined;
    localAlternatives.value = [...(props?.options ?? [{ id: createOptionId(), text: 'Opção 1' }])];
  }

  function addAlternative() {
    const newItem: CheckboxOption = {
      id: createOptionId(),
      text: `Opção ${localAlternatives.value.length + 1}`,
    };
    localAlternatives.value = [...localAlternatives.value, newItem];
  }

  function removeAlternative(index: number) {
    if (localAlternatives.value.length <= 1) return;
    localAlternatives.value = localAlternatives.value.filter((_, i) => i !== index);
  }

  function updateAlternative(index: number, text: string) {
    localAlternatives.value = localAlternatives.value.map((alt, i) =>
      i === index ? { ...alt, text } : alt
    );
  }

  function save() {
    const currentProps = selectedElement.value?.properties as CheckboxProperties;
    updateElement({
      properties: {
        ...currentProps,
        options: localAlternatives.value,
      } as CheckboxProperties,
    });
  }

  return {
    localAlternatives,
    totalCount,
    initFromElement,
    addAlternative,
    removeAlternative,
    updateAlternative,
    save,
  };
}
